import React, { useState, useCallback, useRef, useEffect } from "react";
import copy from "copy-to-clipboard";
import ToggleSwitch from './ToggleSwitch.jsx';
import { TableRow, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    MoreVertical,
    Edit,
    UserX,
    Key,
    Users,
    Link as LinkIcon,
    Mail,
    AlertCircle,
    Phone as PhoneIcon,
    Loader2
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/* --- Shared helpers ------------------------------------------------------- */
function StatusBadge({ status }) {
    if (status === "Active")
        return <Badge variant="success">Active</Badge>;
    if (status === "Profile Pending")
        return <Badge variant="warning">Profile Pending</Badge>;
    return <Badge variant="secondary">{status}</Badge>;
}

function MenuButton({ icon, label, onClick, className = '' }) {
    return (
        <Button
            type="button"
            variant="ghost"
            onClick={onClick}
            className={`w-full justify-start gap-2 px-2 py-1.5 h-auto text-sm font-normal rounded ${className}`}
        >
            <span className="h-4 w-4 shrink-0">{icon}</span>
            {label}
        </Button>
    );
}

/* --- Desktop table row --------------------------------------------------- */
function Users_no_row({
    userName, firstName, lastName, email, groupNumber, userStatus,
    userId, referral_Link_Request_Status, isMigrated, setupLink,
    paymentStatus, mailStatus, phone, tierCommission_Setting, secondarycard,
    tabName, isEmailActivated,
    toggle_add_edit_popup, activate_deactivate, ClosePopUp, toggle_refferal_pop,
    resendActivationApi, TierCommission, SecondaryCard, setStateOfParent, userPagin
}) {
    const [copyTooltip, setCopyTooltip] = useState('Migration Link - Click to Copy');
    const [copyTooltip1, setCopyTooltip1] = useState('Copy Activation Link');
    const [copyPhoneTooltip, setCopyPhoneTooltip] = useState('Copy Phone Number');
    const [loading, setLoading] = useState(false);

    const Copytext = useCallback((text) => {
        copy(window.location.origin + "/" + text);
        setCopyTooltip("Copied");
        setTimeout(() => setCopyTooltip("Migration Link - Click to Copy"), 600);
    }, []);

    const Copytext1 = useCallback((text) => {
        copy(window.location.origin + "/" + text);
        setCopyTooltip1("Copied");
        setTimeout(() => setCopyTooltip1("Copy Activation Link"), 600);
    }, []);

    const Copyphone = useCallback((text) => {
        copy(text);
        setCopyPhoneTooltip("Copied");
        setTimeout(() => setCopyPhoneTooltip("Copy Phone Number"), 600);
    }, []);

    const tiercommissionFunc = async () => {
        setLoading(true);
        await TierCommission({ userId, userName, email, currentValue: tierCommission_Setting });
        setLoading(false);
    };

    const secondarycardFunc = async () => {
        setLoading(true);
        await SecondaryCard({ userId, userName, email, currentValue: secondarycard });
        setLoading(false);
    };

    if (!userName) {
        return (
            <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No Record Found
                </TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow className="hover:bg-muted/50">
            <TableCell className="font-medium text-primary">{userName}</TableCell>
            <TableCell className="text-primary">
                {firstName} {lastName}
            </TableCell>
            <TableCell className="text-primary max-w-xs">
                <div className="flex items-center gap-2">
                    <span className="truncate">{email}</span>
                    {mailStatus === 'Risky' && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <AlertCircle className="h-4 w-4 text-destructive cursor-help shrink-0" />
                                </TooltipTrigger>
                                <TooltipContent>This email was flagged as risky</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
            </TableCell>
            <TableCell className="text-primary">{groupNumber}</TableCell>
            <TableCell><StatusBadge status={userStatus} /></TableCell>
            <TableCell>
                <ToggleSwitch
                    tabName={tabName}
                    rowPagin={userPagin}
                    userstatus={userStatus}
                    numbering={userId}
                    payment={paymentStatus}
                    username={userName}
                    email={email}
                    setStateOfParent={setStateOfParent}
                />
            </TableCell>
            <TableCell className="text-right">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        {loading && (
                            <div className="flex items-center justify-center p-2">
                                <Loader2 className="h-4 w-4 animate-spin text-accent" />
                            </div>
                        )}

                        {!loading && (
                            <>
                                {userStatus === "Active" && (
                                    <>
                                        <DropdownMenuItem
                                            onClick={() => toggle_add_edit_popup({
                                                userFullname: (firstName ? firstName + " " + lastName : userName),
                                                repid: userId
                                            })}
                                        >
                                            <Edit className="mr-2 h-4 w-4" />
                                            Edit User
                                        </DropdownMenuItem>

                                        <DropdownMenuItem
                                            onClick={() => activate_deactivate({ status: 'Deactivate', repid: userId })}
                                            className="text-destructive focus:text-destructive"
                                        >
                                            <UserX className="mr-2 h-4 w-4" />
                                            Deactivate User
                                        </DropdownMenuItem>

                                        <DropdownMenuItem
                                            onClick={() => ClosePopUp({ repid: userId })}
                                        >
                                            <Key className="mr-2 h-4 w-4" />
                                            Change Credentials
                                        </DropdownMenuItem>

                                        <DropdownMenuItem
                                            onClick={() => toggle_refferal_pop({
                                                userFullname: (firstName ? firstName + " " + lastName : userName),
                                                numbering: userId
                                            })}
                                        >
                                            <Users className="mr-2 h-4 w-4" />
                                            Referrals
                                        </DropdownMenuItem>

                                        <DropdownMenuSeparator />

                                        <div className="px-2 py-1.5 text-sm">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={!!tierCommission_Setting}
                                                    onChange={tiercommissionFunc}
                                                    className="rounded"
                                                />
                                                <span className="text-sm">Commission Tiers</span>
                                            </label>
                                        </div>

                                        <div className="px-2 py-1.5 text-sm">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={!!secondarycard}
                                                    onChange={secondarycardFunc}
                                                    className="rounded"
                                                />
                                                <span className="text-sm">Display Secondary Cards</span>
                                            </label>
                                        </div>

                                        {isMigrated === false && setupLink && setupLink !== 'n/a' && (
                                            <>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem onClick={() => Copytext(setupLink)}>
                                                    <LinkIcon className="mr-2 h-4 w-4" />
                                                    {copyTooltip}
                                                </DropdownMenuItem>
                                            </>
                                        )}
                                    </>
                                )}

                                {isEmailActivated && userStatus !== "Profile Pending" && userStatus !== "Active" && (
                                    <DropdownMenuItem
                                        onClick={() => activate_deactivate({ status: 'Activate', repid: userId })}
                                        className="text-green-600 focus:text-green-600"
                                    >
                                        <Users className="mr-2 h-4 w-4" />
                                        Activate User
                                    </DropdownMenuItem>
                                )}

                                {userStatus === "Profile Pending" && userName.indexOf('rxh-user-') > -1 && setupLink && setupLink !== 'n/a' && (
                                    <DropdownMenuItem onClick={() => Copytext(setupLink)}>
                                        <LinkIcon className="mr-2 h-4 w-4" />
                                        {copyTooltip}
                                    </DropdownMenuItem>
                                )}

                                {!isEmailActivated && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onClick={() => resendActivationApi({ userId, userName })}
                                        >
                                            <Mail className="mr-2 h-4 w-4" />
                                            Resend Verification
                                        </DropdownMenuItem>

                                        {setupLink && setupLink !== 'n/a' && (
                                            <DropdownMenuItem onClick={() => Copytext1(setupLink)}>
                                                <LinkIcon className="mr-2 h-4 w-4" />
                                                {copyTooltip1}
                                            </DropdownMenuItem>
                                        )}

                                        <DropdownMenuItem onClick={() => Copyphone(phone)}>
                                            <PhoneIcon className="mr-2 h-4 w-4" />
                                            {copyPhoneTooltip}
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </TableCell>
        </TableRow>
    );
}

/* --- Mobile card ---------------------------------------------------------- */
export function UserCard({
    userName, firstName, lastName, email, groupNumber, userStatus,
    userId, isMigrated, setupLink,
    paymentStatus, mailStatus, phone, tierCommission_Setting, secondarycard,
    tabName, isEmailActivated,
    toggle_add_edit_popup, activate_deactivate, ClosePopUp, toggle_refferal_pop,
    resendActivationApi, TierCommission, SecondaryCard, setStateOfParent
}) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [copyTooltip, setCopyTooltip] = useState('Copy Migration Link');
    const [copyTooltip1, setCopyTooltip1] = useState('Copy Activation Link');
    const [copyPhoneTooltip, setCopyPhoneTooltip] = useState('Copy Phone');
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!menuOpen) return;
        const handleOutside = (e) => {
            if (!wrapperRef.current?.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', handleOutside);
        document.addEventListener('touchstart', handleOutside);
        return () => {
            document.removeEventListener('mousedown', handleOutside);
            document.removeEventListener('touchstart', handleOutside);
        };
    }, [menuOpen]);

    if (!userName) return null;

    const handleItem = (fn) => (e) => {
        setMenuOpen(false);
        fn(e);
    };

    const tiercommissionFunc = async () => {
        setLoading(true);
        setMenuOpen(false);
        await TierCommission({ userId, userName, email, currentValue: tierCommission_Setting });
        setLoading(false);
    };

    const secondarycardFunc = async () => {
        setLoading(true);
        setMenuOpen(false);
        await SecondaryCard({ userId, userName, email, currentValue: secondarycard });
        setLoading(false);
    };

    const Copytext = (text) => {
        copy(window.location.origin + "/" + text);
        setCopyTooltip("Copied!");
        setMenuOpen(false);
        setTimeout(() => setCopyTooltip("Copy Migration Link"), 800);
    };

    const Copytext1 = (text) => {
        copy(window.location.origin + "/" + text);
        setCopyTooltip1("Copied!");
        setMenuOpen(false);
        setTimeout(() => setCopyTooltip1("Copy Activation Link"), 800);
    };

    const Copyphone = (text) => {
        copy(text);
        setCopyPhoneTooltip("Copied!");
        setMenuOpen(false);
        setTimeout(() => setCopyPhoneTooltip("Copy Phone"), 800);
    };

    return (
        <div className="bg-card rounded-lg border border-border shadow-sm p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
                <div className="grid grid-cols-2 flex-1 gap-x-4 gap-y-3 min-w-0">
                    <div>
                        <p className="text-xs text-muted-foreground">Username</p>
                        <p className="text-sm font-semibold text-accent">{userName}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Full Name</p>
                        <p className="text-sm font-semibold">{firstName} {lastName}</p>
                    </div>
                    <div className="col-span-2 min-w-0">
                        <p className="text-xs text-muted-foreground">Email</p>
                        <div className="flex items-center gap-1 min-w-0">
                            <p className="text-sm truncate">{email}</p>
                            {mailStatus === 'Risky' && (
                                <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" title="This email was flagged as risky" />
                            )}
                        </div>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Group Number</p>
                        <p className="text-sm">{groupNumber || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Status</p>
                        <StatusBadge status={userStatus} />
                    </div>
                    <div className="col-span-2">
                        <p className="text-xs text-muted-foreground mb-1">Approve Payment</p>
                        <ToggleSwitch
                            tabName={tabName}
                            userstatus={userStatus}
                            numbering={userId}
                            payment={paymentStatus}
                            username={userName}
                            email={email}
                            setStateOfParent={setStateOfParent}
                        />
                    </div>
                </div>

                {/* Three-dot action menu */}
                <div className="relative shrink-0" ref={wrapperRef}>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 -mt-1"
                        onClick={() => setMenuOpen(o => !o)}
                    >
                        <MoreVertical className="h-4 w-4" />
                    </Button>

                    {menuOpen && (
                        <div className="absolute right-0 top-full mt-1 z-50 min-w-[11rem] rounded-md border bg-popover shadow-md p-1">
                            {loading ? (
                                <div className="flex items-center justify-center p-2">
                                    <Loader2 className="h-4 w-4 animate-spin text-accent" />
                                </div>
                            ) : (
                                <>
                                    {userStatus === "Active" && (
                                        <>
                                            <MenuButton
                                                icon={<Edit className="h-4 w-4" />}
                                                label="Edit User"
                                                onClick={handleItem(() => toggle_add_edit_popup({
                                                    userFullname: (firstName ? firstName + " " + lastName : userName),
                                                    repid: userId
                                                }))}
                                            />
                                            <MenuButton
                                                icon={<UserX className="h-4 w-4" />}
                                                label="Deactivate User"
                                                className="text-destructive"
                                                onClick={handleItem(() => activate_deactivate({ status: 'Deactivate', repid: userId }))}
                                            />
                                            <MenuButton
                                                icon={<Key className="h-4 w-4" />}
                                                label="Change Credentials"
                                                onClick={handleItem(() => ClosePopUp({ repid: userId }))}
                                            />
                                            <MenuButton
                                                icon={<Users className="h-4 w-4" />}
                                                label="Referrals"
                                                onClick={handleItem(() => toggle_refferal_pop({
                                                    userFullname: (firstName ? firstName + " " + lastName : userName),
                                                    numbering: userId
                                                }))}
                                            />
                                            <div className="border-t my-1" />
                                            <div className="px-2 py-1.5">
                                                <label className="flex items-center gap-2 cursor-pointer text-sm" onClick={tiercommissionFunc}>
                                                    <input type="checkbox" checked={!!tierCommission_Setting} readOnly className="rounded border-border pointer-events-none" />
                                                    Commission Tiers
                                                </label>
                                            </div>
                                            <div className="px-2 py-1.5">
                                                <label className="flex items-center gap-2 cursor-pointer text-sm" onClick={secondarycardFunc}>
                                                    <input type="checkbox" checked={!!secondarycard} readOnly className="rounded border-border pointer-events-none" />
                                                    Secondary Cards
                                                </label>
                                            </div>
                                            {isMigrated === false && setupLink && setupLink !== 'n/a' && (
                                                <>
                                                    <div className="border-t my-1" />
                                                    <MenuButton
                                                        icon={<LinkIcon className="h-4 w-4" />}
                                                        label={copyTooltip}
                                                        onClick={() => Copytext(setupLink)}
                                                    />
                                                </>
                                            )}
                                        </>
                                    )}
                                    {isEmailActivated && userStatus !== "Profile Pending" && userStatus !== "Active" && (
                                        <MenuButton
                                            icon={<Users className="h-4 w-4" />}
                                            label="Activate User"
                                            className="text-green-600"
                                            onClick={handleItem(() => activate_deactivate({ status: 'Activate', repid: userId }))}
                                        />
                                    )}
                                    {userStatus === "Profile Pending" && userName.indexOf('rxh-user-') > -1 && setupLink && setupLink !== 'n/a' && (
                                        <MenuButton
                                            icon={<LinkIcon className="h-4 w-4" />}
                                            label={copyTooltip}
                                            onClick={() => Copytext(setupLink)}
                                        />
                                    )}
                                    {!isEmailActivated && (
                                        <>
                                            <div className="border-t my-1" />
                                            <MenuButton
                                                icon={<Mail className="h-4 w-4" />}
                                                label="Resend Verification"
                                                onClick={handleItem(() => resendActivationApi({ userId, userName }))}
                                            />
                                            {setupLink && setupLink !== 'n/a' && (
                                                <MenuButton
                                                    icon={<LinkIcon className="h-4 w-4" />}
                                                    label={copyTooltip1}
                                                    onClick={() => Copytext1(setupLink)}
                                                />
                                            )}
                                            <MenuButton
                                                icon={<PhoneIcon className="h-4 w-4" />}
                                                label={copyPhoneTooltip}
                                                onClick={() => Copyphone(phone)}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Users_no_row;


